import React, { useState } from 'react';
import { X, KeyRound, MessageCircle, ShieldAlert, CheckCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Config } from '../../types';

interface WhatsAppVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  config?: Config;
  userPhone?: string; // If we already know the user's phone, we can prefill it
}

// WhatsApp SVG icon (official brand icon)
function WhatsAppIcon({ size = 28, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// Argentine phone format helper
// Valid Argentine mobile: 10 digits (area code without 0 + number without 15)
// e.g. 2984335215 -> area 298, number 4335215
function formatArgentinePhone(raw: string): string {
  // strip everything except digits
  return raw.replace(/\D/g, '').slice(0, 10);
}

function isValidArgentinePhone(digits: string): boolean {
  if (digits.length !== 10) return false;
  // Argentine area codes start with 2, 3, or 11 (Buenos Aires)
  const validPrefixes = /^(11|2[0-9]|3[0-9])/;
  return validPrefixes.test(digits);
}

function getPhoneHint(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length < 10) return `Faltan ${10 - digits.length} dígitos`;
  if (!isValidArgentinePhone(digits)) return 'Código de área inválido para Argentina';
  return '';
}

export function WhatsAppVerificationModal({ isOpen, onClose, onSuccess, config, userPhone = '' }: WhatsAppVerificationModalProps) {
  const [otpStep, setOtpStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState(userPhone);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showVerifiedPopup, setShowVerifiedPopup] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setOtpStep('phone');
      setError('');
      setSuccessMessage('');
      setOtp('');
      setShowVerifiedPopup(false);
      if (userPhone && !phone) {
         setPhone(userPhone);
      }
    }
  }, [isOpen, userPhone]);

  if (!isOpen) return null;

  const auth = getAuth();
  const primaryColor = config?.primaryColor || '#eab308';

  const phoneHint = getPhoneHint(phone);
  const phoneValid = isValidArgentinePhone(phone);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatArgentinePhone(e.target.value);
    setPhone(formatted);
    if (error) setError('');
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setError('Ingresa un número de WhatsApp argentino válido (10 dígitos, sin 0 ni 15).');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      // Check if phone is already verified by another user
      const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore');
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('phone', '==', phone), where('phoneVerified', '==', true));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const isOtherUser = querySnapshot.docs.some(doc => doc.id !== auth.currentUser?.uid);
        if (isOtherUser) {
          setError('Este número ya está verificado en otra cuenta.');
          setLoading(false);
          return;
        }
      }

      const functionsInstance = getFunctions();
      const sendWhatsAppOtp = httpsCallable(functionsInstance, 'sendWhatsAppOtp');
      await sendWhatsAppOtp({ phone });
      
      setOtpStep('code');
      setSuccessMessage('¡Código enviado a tu WhatsApp!');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Error al enviar el código de verificación.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Ingresa el código de 6 dígitos.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const functionsInstance = getFunctions();
      const verifyWhatsAppOtp = httpsCallable(functionsInstance, 'verifyWhatsAppOtp');
      const response: any = await verifyWhatsAppOtp({ phone, otp });
      
      if (response.data?.customToken || response.data?.success) {
        // Guardamos el teléfono en el perfil del usuario actual (que ya está logueado)
        try {
          if (auth.currentUser) {
             const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
             const db = getFirestore();
             const userId = auth.currentUser.uid;
             
             // Actualizar en users
             await updateDoc(doc(db, 'users', userId), { phone, phoneVerified: true });
             // Actualizar en players
             await updateDoc(doc(db, 'players', userId), { phone, phoneVerified: true });
          }
        } catch (e) {
          console.error('Error saving phone to profile', e);
        }

        // Show success popup before closing
        setShowVerifiedPopup(true);
        setTimeout(() => {
          setShowVerifiedPopup(false);
          onSuccess();
          onClose();
        }, 2200);
      } else {
        setError('Error al verificar.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Código incorrecto o expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-md animate-in fade-in duration-300">
        <div className="bg-[#1e293b]/95 backdrop-blur-xl rounded-[2rem] md:rounded-[var(--radius-large)] border border-white/10 w-full max-w-md shadow-2xl relative overflow-hidden">
          
          <div 
            className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-[80px] opacity-20 pointer-events-none"
            style={{ backgroundColor: primaryColor }}
          />

          <button onClick={onClose} className="absolute top-6 right-6 p-2 bg-black/40 hover:bg-white/10 rounded-full transition-all text-white/50 hover:text-white z-20 shadow-lg">
            <X size={20} />
          </button>

          <div className="p-6 md:p-8 relative z-10 flex flex-col items-center">
            {/* WhatsApp icon */}
            <div 
               className="w-16 h-16 rounded-full flex items-center justify-center mb-4 border"
               style={{ backgroundColor: '#25D36620', borderColor: '#25D36640', color: '#25D366' }}
            >
               <WhatsAppIcon size={28} color="#25D366" />
            </div>

            <h2 className="text-xl md:text-2xl font-black uppercase tracking-wider text-white text-center mb-1">
              Verifica tu WhatsApp
            </h2>
            <p className="opacity-60 text-[10px] md:text-sm font-bold text-center mb-6 max-w-[280px]">
              Para continuar, necesitamos asegurarnos de que tu número de WhatsApp sea real.
            </p>

            {error && (
              <div className="w-full bg-red-500/10 text-red-400 text-xs font-bold text-center px-4 py-3 rounded-xl border border-red-500/20 mb-6 flex items-center gap-2 justify-center">
                <ShieldAlert size={16} className="shrink-0" /> 
                <span>{error}</span>
              </div>
            )}

            {successMessage && (
              <div className="w-full bg-[#bef264]/10 text-[#bef264] text-xs font-bold text-center px-4 py-3 rounded-xl border border-[#bef264]/20 mb-6 flex items-center gap-2 justify-center">
                <MessageCircle size={16} className="shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            <div className="w-full space-y-4">
              {otpStep === 'phone' ? (
                <form onSubmit={handleSendOtp} className="w-full space-y-3">
                  {/* Phone input with Argentine format guide */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 px-1">
                      <span className="text-[9px] font-black uppercase tracking-wider text-white/50">Número argentino</span>
                      <span className="text-[9px] font-bold text-white/30">sin 0 ni 15</span>
                    </div>
                    <div className="relative group">
                      {/* Fixed +54 prefix */}
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                        <span className="text-sm font-black text-white/40">🇦🇷</span>
                        <span className="text-sm font-black text-white/40">+54</span>
                        <span className="w-px h-5 bg-white/20 ml-1" />
                      </div>
                      <input
                        type="tel"
                        required
                        inputMode="numeric"
                        placeholder="ej: 2984123456"
                        value={phone}
                        onChange={handlePhoneChange}
                        className={`w-full bg-black/40 border rounded-xl pl-24 pr-4 py-3.5 focus:border-white/50 outline-none font-bold text-sm text-white transition-colors ${
                          phone.length === 10
                            ? phoneValid
                              ? 'border-emerald-500/50'
                              : 'border-red-500/50'
                            : 'border-white/10'
                        }`}
                      />
                      {/* Live digit counter */}
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black tabular-nums ${
                        phone.length === 10 ? (phoneValid ? 'text-emerald-400' : 'text-red-400') : 'text-white/20'
                      }`}>
                        {phone.length}/10
                      </span>
                    </div>
                    {/* Hint below */}
                    {phoneHint && (
                      <p className={`text-[10px] font-bold ml-1 ${phoneValid ? 'text-emerald-400' : 'text-white/40'}`}>
                        {phoneHint}
                      </p>
                    )}
                    {phone.length === 10 && phoneValid && (
                      <p className="text-[10px] font-bold ml-1 text-emerald-400 flex items-center gap-1">
                        ✓ Número válido: +54 {phone}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !phoneValid}
                    className="w-full mt-1 flex items-center justify-center gap-2 text-white font-black py-4 rounded-xl transition-all uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(37,211,102,0.3)] disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
                    style={{ backgroundColor: '#25D366' }}
                  >
                    <WhatsAppIcon size={18} color="white" />
                    {loading ? 'Enviando...' : 'Recibir Código'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="w-full space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between px-1">
                      <span className="text-[9px] font-black uppercase tracking-wider text-white/50">Código recibido en WhatsApp</span>
                      <span className="text-[9px] font-bold text-white/30">+54 {phone}</span>
                    </div>
                    <div className="relative group">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30 group-focus-within:opacity-100 transition-opacity" style={{ color: primaryColor }} size={18} />
                      <input
                        type="text"
                        required
                        inputMode="numeric"
                        placeholder="• • • • • •"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 focus:border-white/50 outline-none font-black text-center tracking-[0.6em] text-xl text-white transition-colors"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full mt-2 text-black font-black py-4 rounded-xl transition-all uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(0,0,0,0.5)] disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {loading ? 'Verificando...' : 'Verificar Número'}
                  </button>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => { setOtpStep('phone'); setOtp(''); setError(''); setSuccessMessage(''); }}
                      className="text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors cursor-pointer"
                    >
                      Modificar número
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success popup */}
      {showVerifiedPopup && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-none">
          <div className="bg-emerald-500 text-black px-8 py-5 rounded-2xl shadow-2xl flex flex-col items-center gap-2 animate-in zoom-in-95 duration-300 pointer-events-auto">
            <CheckCircle size={36} strokeWidth={2.5} />
            <p className="font-black uppercase tracking-widest text-sm">¡Teléfono Verificado!</p>
            <p className="font-bold text-xs opacity-70">Tu número ha sido confirmado con éxito.</p>
          </div>
        </div>
      )}
    </>
  );
}
